/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button, { LinkButton } from '@atlaskit/button/new';
import { IconTile } from '@atlaskit/icon';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import VideoIcon from '@atlaskit/icon/core/video';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Flex, Grid, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	type ExternalCalendarEvent,
	type SearchResultCalendarEventPartial,
	type SearchResultGraphDocument,
} from '@atlassian/dynamic-cards-search-client';

import { EventDate } from '../../../../../../../common/ui/event-date';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { eventTimeRange } from '../../../../../../../common/utils/event-time';
import { enrichExcerpt, isNonPublic } from '../../../../../../../common/utils/search-result-utils';
import {
	useBootstrap,
	usePrimaryProduct,
	useSelectedProduct,
} from '../../../../../../../controllers/store';

import { messages } from './messages';

interface CalendarEventSearchResultProps {
	icon: React.ReactNode;
	partial: SearchResultCalendarEventPartial;
	/** The position in the results list shown to the user. */
	position?: number;
	product?: string;
}

interface CalendarEventMeetingProps {
	fromDate?: string;
	meetingLink?: string;
	toDate?: string;
}

type CalendarEventActionProps = CalendarEventMeetingProps &
	Required<Pick<SearchResultCalendarEventPartial, 'linkedEntities'>>;

type RelatedCalendarEvents = Required<
	Pick<SearchResultGraphDocument<ExternalCalendarEvent>, 'entity' | 'title' | 'url'>
>;

const RELATED_EVENTS_STATE = {
	UPCOMING: 'upcoming',
	PAST: 'past',
	CLOSED: 'closed',
} as const;
type RelatedEventsState = (typeof RELATED_EVENTS_STATE)[keyof typeof RELATED_EVENTS_STATE];

export const CalendarEventMeetingLink = ({
	fromDate,
	toDate,
	meetingLink,
}: CalendarEventMeetingProps): JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!fromDate || !meetingLink || !toDate) {
		return null;
	}

	const currentDate = new Date();
	const startDate = new Date(fromDate);
	startDate.setHours(startDate.getHours() - 2); // Checks if the event is within 2 hours
	const endDate = new Date(toDate);
	const eventIsWithin2hoursOrIsOngoing = startDate < currentDate && currentDate < endDate;

	return (
		<LinkButton
			iconBefore={() => (
				<VideoIcon
					color="var(--ds-icon-subtle)"
					label={''} // Presentation Only
				/>
			)}
			href={meetingLink}
		>
			{eventIsWithin2hoursOrIsOngoing
				? formatMessage(messages.joinMeetingLink)
				: formatMessage(messages.meetingLink)}
		</LinkButton>
	);
};

const calendarEventLinkStyles = xcss({
	color: 'color.link',
	textDecoration: 'none',
	':hover': {
		color: 'color.link',
		textDecoration: 'underline',
	},
	':active': {
		textDecoration: 'none',
	},
	':visited': {
		color: 'color.link.visited',
	},
	':visited:active': {
		color: 'color.link.visited.pressed',
	},
	display: 'flex',
});

const relatedCalendarEventMetadataStyles = xcss({
	gridColumn: 2,
	alignItems: 'center',
});

const RelatedCalendarEvents = ({ calendarEvents }: { calendarEvents: RelatedCalendarEvents[] }) => {
	const { formatMessage } = useIntl();

	return (
		<Grid gap="space.200">
			{calendarEvents.map((calendarEvent) => {
				const { entity, title, url } = calendarEvent;

				const labelMessageComponent = (
					<Text size="small">{formatMessage(messages.eventLabel)}</Text>
				);

				const eventDateComponent = (
					<Text size="small">
						<EventDate date={entity.eventStartTime} />
					</Text>
				);

				const eventTimeComponent =
					entity.eventStartTime && entity.eventEndTime ? (
						<Text size="small">
							{eventTimeRange({
								from: entity.eventStartTime,
								to: entity.eventEndTime,
							})}
						</Text>
					) : null;

				const meetingLink = entity.videoMeetingURL ? (
					<Text size="small">
						<Anchor xcss={calendarEventLinkStyles} href={entity.videoMeetingURL}>
							{formatMessage(messages.meetingLink)}
						</Anchor>
					</Text>
				) : null;

				return (
					<Grid templateColumns="min-content 1fr" alignItems="center" columnGap="space.100">
						{fg('confluence_frontend_tile_object_system_migration') ? (
							<CalendarIcon
								label={formatMessage(messages.recurringEventIcon)}
								color={token('color.icon.accent.blue')}
							/>
						) : (
							<IconTile
								size="16"
								icon={CalendarIcon}
								label={formatMessage(messages.recurringEventIcon)}
								appearance="blue"
							/>
						)}
						<Text size="small" weight="semibold">
							<Anchor xcss={calendarEventLinkStyles} href={url}>
								{title}
							</Anchor>
						</Text>

						<Flex xcss={relatedCalendarEventMetadataStyles}>
							<SearchMetadata
								parts={[
									['calendar-label', labelMessageComponent],
									['event-date', eventDateComponent],
									['event-time', eventTimeComponent],
									['meeting-link', meetingLink],
								]}
							/>
						</Flex>
					</Grid>
				);
			})}
		</Grid>
	);
};

const calendarEventActionStyles = xcss({
	display: 'flex',
	gap: 'space.100',
});

const CalendarEventActions = ({
	fromDate,
	toDate,
	meetingLink,
	linkedEntities,
}: CalendarEventActionProps) => {
	const { formatMessage } = useIntl();
	const [relatedEventsState, setEventsToDisplay] = useState<RelatedEventsState>(
		RELATED_EVENTS_STATE.CLOSED,
	);

	const isUpcomingEventsSelected = relatedEventsState === RELATED_EVENTS_STATE.UPCOMING;
	const isPastEventsSelected = relatedEventsState === RELATED_EVENTS_STATE.PAST;

	const data = useMemo(() => {
		const currentDate = new Date();

		const linkedCalendarEvents = linkedEntities.flatMap((linkedEntity) =>
			linkedEntity.entity
				? [{ title: linkedEntity.title, url: linkedEntity.url, entity: linkedEntity.entity }]
				: [],
		);

		const [pastEvents, upcomingEvents] = linkedCalendarEvents.reduce<
			[RelatedCalendarEvents[], RelatedCalendarEvents[]]
		>(
			(events, currentEvent) => {
				const isPastEvent = new Date(currentEvent.entity.eventStartTime) < currentDate;

				if (isPastEvent) {
					return [events[0].concat(currentEvent), events[1]];
				}
				return [events[0], events[1].concat(currentEvent)];
			},
			[[], []],
		);

		return {
			pastEvents,
			upcomingEvents,
		};
	}, [linkedEntities]);

	return (
		<Grid gap="space.200">
			<Box xcss={calendarEventActionStyles}>
				<CalendarEventMeetingLink fromDate={fromDate} toDate={toDate} meetingLink={meetingLink} />

				{!!data.upcomingEvents.length && (
					<Button
						iconAfter={(iconProps) =>
							isUpcomingEventsSelected ? (
								<ChevronDownIcon {...iconProps} size="small" />
							) : (
								<ChevronRightIcon {...iconProps} size="small" />
							)
						}
						onClick={() =>
							setEventsToDisplay(
								isUpcomingEventsSelected
									? RELATED_EVENTS_STATE.CLOSED
									: RELATED_EVENTS_STATE.UPCOMING,
							)
						}
						isSelected={isUpcomingEventsSelected}
					>
						{formatMessage(messages.upcomingEventsButton)}
					</Button>
				)}

				{!!data.pastEvents.length && (
					<Button
						iconAfter={(iconProps) =>
							isPastEventsSelected ? (
								<ChevronDownIcon {...iconProps} size="small" />
							) : (
								<ChevronRightIcon {...iconProps} size="small" />
							)
						}
						onClick={() =>
							setEventsToDisplay(
								isPastEventsSelected ? RELATED_EVENTS_STATE.CLOSED : RELATED_EVENTS_STATE.PAST,
							)
						}
						isSelected={isPastEventsSelected}
					>
						{formatMessage(messages.pastEventsButton)}
					</Button>
				)}
			</Box>

			{relatedEventsState === RELATED_EVENTS_STATE.UPCOMING && (
				<RelatedCalendarEvents calendarEvents={data.upcomingEvents} />
			)}
			{relatedEventsState === RELATED_EVENTS_STATE.PAST && (
				<RelatedCalendarEvents calendarEvents={data.pastEvents} />
			)}
		</Grid>
	);
};

export const CalendarEventSearchResult = ({
	partial,
	position,
	icon,
	product,
}: CalendarEventSearchResultProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();

	const { url, permissionLevel, entity, title, linkedEntities } = partial;

	const calendarEvent = useMemo(() => {
		const eventTime =
			entity?.eventStartTime && entity?.eventEndTime
				? eventTimeRange({ from: entity?.eventStartTime, to: entity?.eventEndTime })
				: null;

		const recurringEvent = entity?.recurringEventID
			? formatMessage(messages.recurringMetadataLabel)
			: null;

		return {
			labelMessage: formatMessage(messages.eventLabel),
			calendarName: entity?.parentCalendar.name ?? null,
			eventTime,
			recurringEvent,
		};
	}, [entity, formatMessage]);

	const eventDateComponent = <EventDate date={entity?.eventStartTime} />;

	const body = <Box paddingBlockEnd="space.100">{partial.description}</Box>;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	return (
		<SearchResult
			id={partial.id}
			icon={icon}
			description={body}
			headerMetadata={
				<SearchMetadata
					product={product}
					productSelected={!!selectedProduct}
					parts={[
						calendarEvent.labelMessage,
						calendarEvent.calendarName,
						['event-date', eventDateComponent],
						['event-time', calendarEvent.eventTime],
						calendarEvent.recurringEvent,
						['padlock', padlockIcon],
					]}
				/>
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			footer={
				<CalendarEventActions
					meetingLink={entity?.videoMeetingURL}
					fromDate={entity?.eventStartTime}
					toDate={entity?.eventEndTime}
					linkedEntities={linkedEntities ?? []}
				/>
			}
		/>
	);
};
